import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue';

// Largest frame-to-frame delta (ms) used as a render-cost sample when auto-tuning the chunk size.
// A throttled/janky frame (e.g. a backgrounded tab where rAF fires at ~1Hz) would otherwise look
// like a hugely expensive chunk and collapse the chunk size to 1, stretching the reveal to one row
// per frame; cap the sample so that can't happen.
const MAX_FRAME_SAMPLE_MS = 50;

interface ProgressiveRowsOptions {
  /** Rows revealed in the first synchronous pass (keep small — this one render is on the
   *  critical path of the response handler). */
  initialCount?: number;
  /** Per-frame wall-clock budget. The chunk size auto-tunes toward this, so frames stay short
   *  in dev (where each row is expensive) and grow large in prod (where rows are cheap). */
  targetFrameMs?: number;
  /** Hard cap on rows revealed in a single frame. */
  maxChunk?: number;
}

interface ProgressiveRows<T> {
  /** The slice of `source` to render right now; grows toward the full set across frames. */
  visibleRows: ComputedRef<T[]>;
}

/**
 * Reveal a large list incrementally across animation frames so the first render never blocks
 * the main thread for the whole set. A big result table (search, track-matching, …) used to
 * render every row in one synchronous pass when its API response arrived, freezing the UI on
 * load. Revealing in chunks lets the thread yield (and the browser paint / handle input)
 * between chunks, so the table fills in while the page stays responsive.
 *
 * The ramp (re)starts when the row SET changes — first load, navigation, or a new result set,
 * detected by a `datasetKey` changing (not by array reference). When a parent merely re-derives
 * the same dataset into a fresh array — e.g. track-matching recomputes its rows on every
 * expand/collapse/queue toggle (same leading live-track row; rows added/removed below it) — the
 * table is NOT collapsed and re-revealed; already-shown rows stay and only added rows ramp in.
 * Keying on reference would snap the table back to initialCount and re-reveal on every such
 * interaction (a visible flicker); keying on a full id-signature would do the same on expand
 * (which legitimately changes the row set), so the key must be stable across re-derivations.
 *
 * `datasetKey` therefore depends on whether the leading row carries a usable id:
 *   - Leading row has a defined, non-null `id`: key on that id ALONE. The leading id is stable
 *     across track-matching's expand/collapse/queue re-derivations, so the table doesn't flicker.
 *   - Leading row has NO usable id (DataTable falls back to a `row-${index}` key, so id-less rows
 *     do occur): key on a signature combining the row COUNT and a cheap content hash of the first
 *     row (`JSON.stringify(rows[0])`). The stringify runs ONLY on this id-less path, never for
 *     id-bearing tables, so the common case stays allocation-free. A genuinely new id-less dataset
 *     (different first-row content and/or a different length) then re-ramps from initialCount
 *     instead of rendering every row at once.
 *
 * Residual edge (by design): two id-BEARING datasets that share the same leading id won't re-ramp
 * — e.g. two different searches whose top row is the same record. This is rare, and the worst case
 * is a single synchronous full render for that one navigation; it's the deliberate cost of NOT
 * re-ramping (and flickering) on track-matching's same-lead-id re-derivations.
 *
 * Small sets (<= initialCount) and environments without requestAnimationFrame (e.g. unit tests)
 * render everything at once.
 */
export function useProgressiveRows<T>(
  source: MaybeRefOrGetter<readonly T[] | undefined | null>,
  options: ProgressiveRowsOptions = {},
): ProgressiveRows<T> {
  const initialCount = options.initialCount ?? 8;
  const targetFrameMs = options.targetFrameMs ?? 24;
  const maxChunk = options.maxChunk ?? 100;

  const revealedCount = ref(0);
  let pendingFrame: number | null = null;

  const canUseRaf = typeof requestAnimationFrame === 'function';

  const stopRamp = (): void => {
    if (pendingFrame !== null) {
      cancelAnimationFrame(pendingFrame);
      pendingFrame = null;
    }
  };

  const rampToTotal = (totalRows: number): void => {
    stopRamp();
    let chunk = initialCount;
    let previousFrameStart: number | null = null;
    const revealNextChunk = (frameStart: number): void => {
      // Steer the chunk toward targetFrameMs using how long the previous reveal frame took
      // (frame-to-frame delta ≈ the cost of rendering the last chunk). Auto-shrinks in dev,
      // grows in prod — no single frame blocks the thread for long.
      if (previousFrameStart !== null) {
        const lastFrameMs = Math.min(frameStart - previousFrameStart, MAX_FRAME_SAMPLE_MS);
        const scaled = (chunk * targetFrameMs) / Math.max(lastFrameMs, 1);
        chunk = Math.max(1, Math.min(maxChunk, Math.round(scaled) || 1));
      }
      previousFrameStart = frameStart;
      revealedCount.value = Math.min(totalRows, revealedCount.value + chunk);
      pendingFrame = revealedCount.value < totalRows ? requestAnimationFrame(revealNextChunk) : null;
    };
    pendingFrame = requestAnimationFrame(revealNextChunk);
  };

  // The discriminator between "new dataset" (re-ramp) and "same dataset re-derived / incrementally
  // changed" (keep what's shown). When the leading row has a usable id, key on it alone (stable
  // across track-matching re-derivations). Otherwise fall back to count + first-row content hash so
  // a genuinely new id-less dataset still re-ramps. See the composable docstring.
  let previousDatasetKey: unknown;
  const datasetKey = (rows: readonly T[] | undefined | null): unknown => {
    if (!rows || rows.length === 0) {
      return undefined;
    }
    const leadRowId = (rows[0] as { id?: unknown }).id;
    if (leadRowId !== undefined && leadRowId !== null) {
      return leadRowId;
    }
    // Id-less path only: combine row count with a cheap content hash of the first row so two
    // different id-less datasets of equal-or-smaller size don't collide. JSON.stringify never runs
    // for id-bearing tables.
    return `${rows.length}:${JSON.stringify(rows[0])}`;
  };

  watch(
    () => toValue(source),
    (rows) => {
      const totalRows = rows?.length ?? 0;
      const currentDatasetKey = datasetKey(rows);
      if (!canUseRaf || totalRows <= initialCount) {
        stopRamp();
        revealedCount.value = totalRows;
        previousDatasetKey = currentDatasetKey;
        return;
      }
      const isNewDataset = revealedCount.value === 0 || currentDatasetKey !== previousDatasetKey;
      previousDatasetKey = currentDatasetKey;
      if (isNewDataset) {
        // First load / new search / sort / filter / page: small synchronous paint, then ramp.
        revealedCount.value = initialCount;
        rampToTotal(totalRows);
      } else if (totalRows > revealedCount.value) {
        // Same dataset, grew (expand a group / queued rows appear): reveal the additions from the
        // current count — don't collapse already-shown rows.
        rampToTotal(totalRows);
      } else {
        // Same dataset, shrank (collapse a group): just drop to the new total, no re-reveal.
        stopRamp();
        revealedCount.value = totalRows;
      }
    },
    { immediate: true },
  );

  onScopeDispose(stopRamp);

  const visibleRows = computed<T[]>(() => {
    const rows = toValue(source);
    if (!rows) {
      return [];
    }
    return rows.slice(0, revealedCount.value) as T[];
  });

  return { visibleRows };
}
